/** Tasks, notes, flags and approvals — S05's client.
 *
 * Its own module rather than a section of `queries.ts`, following the structure the review
 * recommends: a feature owns its API calls and its query-key factory. `queries.ts` is the
 * inventory's, and mixing the two would make the cache keys harder to reason about at exactly the
 * moment they matter — a mutation here has to invalidate a figure's work without invalidating the
 * figure.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { queueKeys } from "./approvals";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type Task = components["schemas"]["TaskOut"];
export type Work = components["schemas"]["WorkOut"];
export type Note = components["schemas"]["NoteOut"];
export type Flag = components["schemas"]["FlagOut"];
/** The **input** enums, not the output shapes. `TaskOut.state` is a plain `string` on the wire;
 * what a mutation may send is a closed set, and using the generated union is what caught three
 * `string`s in this file before they reached a request. */
export type TaskState = components["schemas"]["TaskState"];
export type TaskPriority = components["schemas"]["Priority"];

export const workKeys = {
  tasks: (mine: boolean, state?: TaskState) => ["work", "tasks", mine, state ?? "all"] as const,
  record: (recordId: string) => ["work", "record", recordId] as const,
};

/** The central queue (EXP-24).
 *
 * `mine` is a flag rather than an id: whose work this is comes from the session on the server, and
 * a client that could name a user would be asserting an authorization answer.
 */
export function useTasks(mine: boolean, state?: TaskState) {
  return useQuery({
    queryKey: workKeys.tasks(mine, state),
    queryFn: async (): Promise<Task[]> => {
      const { data, error, response } = await api.GET("/api/v1/tasks", {
        params: { query: { mine, ...(state ? { state } : {}) } },
      });
      // Swallowed, this read as *"nothing open is assigned to you"* — the opposite of the truth
      // when the request for that list failed.
      if (error) throw refusalFrom(response, error, "The work queue could not be read.");
      return data ?? [];
    },
  });
}

/** Everything attached to one record: the workflow tab, in one request.
 *
 * One query rather than four, because the tab shows them together — four would render the counts
 * at four different instants, and a count that disagrees with the list beneath it is the kind of
 * thing a reader stops trusting entirely.
 */
export function useRecordWork(recordId: string | undefined) {
  return useQuery({
    queryKey: workKeys.record(recordId ?? "none"),
    enabled: Boolean(recordId),
    queryFn: async (): Promise<Work | null> => {
      if (!recordId) return null;
      const { data, error, response } = await api.GET("/api/v1/records/{record_id}/work", {
        params: { path: { record_id: recordId } },
      });
      // 404 means "not a record you can see", which is a state to render rather than a failure.
      if (response.status === 404) return null;
      if (error) throw refusalFrom(response, error, "This record's workflow could not be read.");
      return data ?? null;
    },
    retry: false,
  });
}

/** Create a task (F-011, G-Wcontrib-14): `POST /tasks` has existed since S05 and nothing called it.
 *
 * Only what the person chose is sent: `record_id` when the page is about a record, `due_date` when one
 * was picked, nothing for an unlinked task (EXP-24: a task to chase a landlord exists before the
 * record it will hang off does). The owner is an id the caller already holds - their own, or an
 * Admin's pick from the member list - and the server decides whether that person may own one, so a
 * refusal carries its code (`refusalFrom`). Invalidates the whole `work` family on success only: the
 * queue, the record's tab, the bell's count and Home's action items are all read from it.
 */
export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (input: {
      title: string;
      ownerId: string;
      priority: TaskPriority;
      recordId?: string;
      dueDate?: string;
    }): Promise<Task> => {
      const { data, error, response } = await api.POST("/api/v1/tasks", {
        body: {
          title: input.title,
          owner_id: input.ownerId,
          priority: input.priority,
          ...(input.recordId ? { record_id: input.recordId } : {}),
          ...(input.dueDate ? { due_date: input.dueDate } : {}),
        },
      });
      if (error || !data) throw refusalFrom(response, error, "That task could not be added.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["work"] });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      taskId: string;
      state?: TaskState;
      priority?: TaskPriority;
      completionNote?: string;
      expectedVersion: number;
    }) => {
      const { data, error, response } = await api.PATCH("/api/v1/tasks/{task_id}", {
        params: { path: { task_id: input.taskId } },
        body: {
          state: input.state ?? null,
          priority: input.priority ?? null,
          completion_note: input.completionNote ?? null,
          owner_id: null,
          due_date: null,
          // Always sent. A client that omits it is protected against a *concurrent* writer by the
          // version column; what this adds is protection against a **stale** one — somebody who
          // opened the queue an hour ago.
          expected_version: input.expectedVersion,
        },
      });
      // **The refusal carries its code** (`refusalFrom`, not `new Error(message)`). A task PATCH
      // always sends `expected_version`, so `stale_version` is its most likely refusal — and a 409
      // rendered as a sentence with a "try again" beside it is the defect EXP-29's second list
      // names: the remedy for a stale decision is to reload and look, never to re-send.
      if (error) throw refusalFrom(response, error, "That task could not be saved.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["work"] });
    },
  });
}

// -------------------------------------------------------------- the record's own workflow tab

export type ApprovalRequest = components["schemas"]["RequestOut"];
export type ApprovalRoute = components["schemas"]["RouteOut"];
export type FlagSeverity = components["schemas"]["FlagSeverity"];
export type ApprovalDecision = components["schemas"]["Decision"];

/** Routes that review this **kind** of record.
 *
 * Narrowed on the server rather than filtered here: `submit` refuses a route whose `applies_to`
 * does not match, so offering one in a dropdown would be offering a choice the product will reject.
 */
export function useApprovalRoutes(appliesTo: string) {
  return useQuery({
    queryKey: ["work", "routes", appliesTo] as const,
    queryFn: async (): Promise<ApprovalRoute[]> => {
      const { data, error, response } = await api.GET("/api/v1/approval-routes", {
        // **A string rather than the generated union**, and deliberately. The kind this is called
        // with comes from `record.kind` over the wire, where it is a `str`, so a client that
        // compiled against yesterday's schema would have to crash or lie to narrow it. The server
        // validates the value — an unknown one is a 422 and an empty list of routes, which is the
        // same thing this page says when a kind has no route configured.
        params: { query: { applies_to: appliesTo as components["schemas"]["RecordKind"] } },
      });
      // Swallowed, an empty list rendered as *"no review route is configured for this kind"* — a
      // claim about the workspace's setup.
      if (error) throw refusalFrom(response, error, "The review routes could not be read.");
      return data ?? [];
    },
  });
}

export function useAddNote(recordId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { body: string; recordVersion?: number; parentId?: string }) => {
      const { data, error, response } = await api.POST("/api/v1/records/{record_id}/notes", {
        params: { path: { record_id: recordId } },
        body: {
          body: input.body,
          parent_id: input.parentId ?? null,
          // Which revision was on screen. A comment about a figure that has since been corrected
          // is a comment about the old figure, and a thread that does not say so misleads whoever
          // reads it next.
          record_version: input.recordVersion ?? null,
        },
      });
      if (error) throw refusalFrom(response, error, "That note could not be saved.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: workKeys.record(recordId) });
    },
  });
}

export function useRaiseFlag(recordId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { severity: FlagSeverity; kind: string; reason: string }) => {
      const { data, error, response } = await api.POST("/api/v1/records/{record_id}/flags", {
        params: { path: { record_id: recordId } },
        body: { severity: input.severity, kind: input.kind, reason: input.reason },
      });
      if (error) throw refusalFrom(response, error, "That flag could not be raised.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: workKeys.record(recordId) });
    },
  });
}

export function useResolveFlag(recordId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { flagId: string; resolution: string }) => {
      const { data, error, response } = await api.POST("/api/v1/flags/{flag_id}/resolve", {
        params: { path: { flag_id: input.flagId } },
        body: { resolution: input.resolution },
      });
      if (error) throw refusalFrom(response, error, "That flag could not be resolved.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: workKeys.record(recordId) });
    },
  });
}

export function useSubmitForApproval(recordId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { routeId: string; recordVersion: number }) => {
      const { data, error, response } = await api.POST("/api/v1/records/{record_id}/approval-requests", {
        params: { path: { record_id: recordId } },
        body: { route_id: input.routeId, record_version: input.recordVersion },
      });
      if (error) throw refusalFrom(response, error, "That could not be submitted for approval.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: workKeys.record(recordId) });
    },
  });
}

export function useDecideStage(recordId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      requestId: string;
      position: number;
      decision: ApprovalDecision;
      rationale: string;
    }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/approval-requests/{request_id}/decisions",
        {
          params: { path: { request_id: input.requestId } },
          body: {
            position: input.position,
            decision: input.decision,
            rationale: input.rationale,
          },
        },
      );
      if (error) throw refusalFrom(response, error, "That decision could not be recorded.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: workKeys.record(recordId) });
    },
  });
}

/** The submitter answers a reviewer's request for a change (ADR-0053 points 3 and 4, G-P5-03): the
 * same request goes back to the same stage. No body - the request's id is the whole of it - and a
 * refusal carries its code (`refusalFrom`) so the screen can say which: a repeat is
 * `not_awaiting_adjustment`, not a failure to retry. Refreshes the record's work and the reviewer
 * queue on success only. */
export function useResubmitApproval(recordId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (requestId: string): Promise<ApprovalRequest> => {
      const { data, error, response } = await api.POST(
        "/api/v1/approval-requests/{request_id}/resubmit",
        { params: { path: { request_id: requestId } } },
      );
      if (error || !data) throw refusalFrom(response, error, "That request could not be resubmitted.");
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: workKeys.record(recordId) }),
        queryClient.invalidateQueries({ queryKey: queueKeys.awaitingMe() }),
      ]);
    },
  });
}

export function useWithdrawApproval(recordId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (requestId: string): Promise<ApprovalRequest> => {
      const { data, error, response } = await api.POST(
        "/api/v1/approval-requests/{request_id}/withdraw",
        { params: { path: { request_id: requestId } } },
      );
      if (error || !data) throw refusalFrom(response, error, "That submission could not be withdrawn.");
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: workKeys.record(recordId) }),
        queryClient.invalidateQueries({ queryKey: queueKeys.awaitingMe() }),
      ]);
    },
  });
}
